import { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { useTranslation } from "../../i18n";
import {
  useSegmentDefinitions,
  useCreateSegment,
  useUpdateSegment,
  useDeleteSegment,
  usePreviewSegmentQuery,
} from "../../features/super/hooks/useCampaigns";
import toast from "react-hot-toast";

const QUERY_PLACEHOLDER = `SELECT c.id AS customer_id
FROM customer c
JOIN registered_customer rc ON rc.customer_id = c.id
WHERE rc.send_marketing = true
  AND c.minutes_balance > 500`;

/**
 * Manager for operator-defined snapshot segments, in its own modal.
 *
 * A segment is the SELECT that produces its audience, pasted instead of being
 * assembled from fixed fields: the built-in segments cover the RFM buckets,
 * and everything else ("VIP whose expiry passed 60-90 days ago and who still
 * has a balance") needs a query. The contract is one statement returning
 * exactly one integer column named `customer_id`.
 *
 * It was a block inside the campaign form, where it pushed the save button
 * off-screen and made the campaign settings scroll past a second subject.
 * It is opened from the page instead — the "Segments" button, scoped to the
 * campaign in focus, or the Create menu, global — which keeps the campaign
 * form about the campaign.
 *
 * Each definition lists the campaigns that select it — the question an
 * operator has before rewriting or deleting one is "who stops matching?", and
 * it is not answerable from the segment alone.
 */
export default function SegmentManagerDialog({
  open,
  onOpenChange,
  campaignId = null,
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useSegmentDefinitions(campaignId);
  const createMutation = useCreateSegment();
  const updateMutation = useUpdateSegment();
  const deleteMutation = useDeleteSegment();
  const previewMutation = usePreviewSegmentQuery();
  const [form, setForm] = useState({ name: "", query: "" });
  const [editingId, setEditingId] = useState(null);
  // The reason the last preview was refused, kept beside the textarea: the
  // operator is editing the query there, and a toast that disappears left the
  // error nowhere near the statement it is about.
  const [previewError, setPreviewError] = useState("");

  const definitions = data?.items ?? [];
  const preview = previewMutation.data;
  // A preview describes the query as it was when it ran; keeping it on screen
  // after the query is edited would show an audience the operator no longer
  // asked for. The comparison ignores the trailing semicolon the server
  // strips, so a fresh preview never reads as stale.
  const previewIsStale =
    preview != null && !sameQuery(preview.query, form.query);

  const reset = useCallback(() => {
    setForm({ name: "", query: "" });
    setEditingId(null);
    setPreviewError("");
    previewMutation.reset();
  }, [previewMutation]);

  // The dialog stays mounted, so an edit abandoned with Esc or an outside click
  // would greet the next open with "Edit segment", a locked name and a
  // "Save query" button — and pasting there would overwrite the definition
  // the operator never meant to touch. Clear the editor on the open edge.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) reset();
    wasOpen.current = open;
  }, [open, reset]);

  const startEditing = (definition) => {
    setEditingId(definition.id);
    setForm({ name: definition.name, query: definition.query });
    previewMutation.reset();
  };

  const handlePreview = async () => {
    if (!form.query.trim()) {
      setPreviewError(t("campaign.segmentQueryRequired"));
      return;
    }
    setPreviewError("");
    try {
      await previewMutation.mutateAsync(form.query);
    } catch (err) {
      // The backend answers with the reason the query was refused (a keyword,
      // a second statement, the wrong column, the database's own message) —
      // show it under the textarea, where the query being fixed is.
      setPreviewError(parseApiError(err) || t("campaign.segmentPreviewFailed"));
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t("campaign.segmentNameRequired"));
      return;
    }
    try {
      await createMutation.mutateAsync({
        name: form.name.trim(),
        campaignId,
        query: form.query,
      });
      toast.success(t("campaign.segmentCreated"));
      reset();
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentCreateFailed")));
    }
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    try {
      await updateMutation.mutateAsync({ id: editingId, query: form.query });
      toast.success(t("campaign.segmentUpdated"));
      reset();
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentUpdateFailed")));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteMutation.mutateAsync(id);
      if (editingId === id) reset();
      toast.success(t("campaign.segmentDeleted"));
    } catch (err) {
      toast.error(describe(err, t("campaign.segmentDeleteFailed")));
    }
  };

  const busy = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {editingId
              ? t("campaign.segmentEditTitle")
              : t("campaign.customSegments")}
          </DialogTitle>
          <DialogDescription>
            {t("campaign.segmentUsageIntro")}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-c-gray-500/60 border border-c-gray-400/20 rounded-lg p-3 text-xs text-c-gray-300 space-y-2">
          <p className="font-medium text-white">
            {t("campaign.segmentHelpContractTitle")}
          </p>
          <pre className="bg-c-gray-600/60 rounded p-2 overflow-x-auto text-[11px] leading-relaxed">
            {`-- один SELECT, одна колонка customer_id
SELECT c.id AS customer_id
FROM customer c
WHERE ...`}
          </pre>
          <p>{t("campaign.segmentHelpContractNote")}</p>
          <p>
            {campaignId == null
              ? t("campaign.segmentScopeGlobal")
              : t("campaign.segmentScopeCampaign")}
          </p>
        </div>

        {isLoading ? (
          <p className="text-c-gray-400 text-xs">{t("common.loading")}</p>
        ) : definitions.length === 0 ? (
          <p className="text-c-gray-400 text-xs">
            {t("campaign.noCustomSegments")}
          </p>
        ) : (
          <ul className="space-y-2">
            {definitions.map((d) => (
              <li
                key={d.id}
                className="bg-c-gray-500/40 rounded px-3 py-2 text-xs space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">
                    <code className="text-accent">{d.name}</code>
                    <span className="text-c-gray-400"> — {describeDef(d)}</span>
                    {d.isGlobal && (
                      <span className="ml-1 text-c-gray-500">
                        ({t("campaign.segmentGlobal")})
                      </span>
                    )}
                  </span>
                  <span className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEditing(d)}
                      disabled={deleteMutation.isPending}
                    >
                      {t("campaign.segmentEdit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(d.id)}
                      disabled={deleteMutation.isPending}
                      aria-label={t("campaign.segmentDelete")}
                    >
                      ✕
                    </Button>
                  </span>
                </div>
                <p className="text-c-gray-400">{usedByText(d.usedBy, t)}</p>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-1">
          <Label className="text-xs" htmlFor="segment-name">
            {t("campaign.segmentName")}
          </Label>
          <Input
            id="segment-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="vip_sleeping_90d"
            disabled={busy || editingId != null}
          />
          {editingId != null && (
            <p className="text-c-gray-500 text-xs">
              {t("campaign.segmentNameImmutable")}
            </p>
          )}
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs" htmlFor="segment-query">
              {t("campaign.segmentQuery")}
            </Label>
            {/* Copies the statement the operator is looking at — the same
                textarea — instead of the generated full-audience query the
                campaign form's button produces. */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => copyQuery(form.query, t)}
              disabled={!form.query.trim()}
            >
              {t("campaign.copySegmentSql")}
            </Button>
          </div>
          <textarea
            id="segment-query"
            value={form.query}
            onChange={(e) => {
              setForm({ ...form, query: e.target.value });
              setPreviewError("");
            }}
            rows={7}
            spellCheck={false}
            placeholder={QUERY_PLACEHOLDER}
            disabled={busy}
            className={`flex w-full rounded-lg border bg-c-gray-500 px-3 py-2 text-sm text-white placeholder:text-c-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent font-mono resize-y ${
              previewError ? "border-red-500/60" : "border-c-gray-400/30"
            }`}
          />
          {previewError ? (
            <p className="text-red-400 text-xs whitespace-pre-wrap">
              {previewError}
            </p>
          ) : (
            <p className="text-c-gray-500 text-xs">
              {t("campaign.segmentQueryHint")}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={handlePreview}
            disabled={previewMutation.isPending || busy}
          >
            {previewMutation.isPending
              ? t("campaign.segmentPreviewing")
              : t("campaign.segmentPreview")}
          </Button>
          {editingId != null ? (
            <>
              <Button
                size="sm"
                onClick={handleUpdate}
                disabled={busy || !form.query.trim()}
              >
                {t("campaign.segmentSave")}
              </Button>
              <Button size="sm" variant="ghost" onClick={reset}>
                {t("campaign.segmentCancelEdit")}
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={handleCreate} disabled={busy}>
              {t("campaign.segmentAdd")}
            </Button>
          )}
        </div>

        {previewIsStale && (
          <p className="text-c-gray-400 text-xs">
            {t("campaign.segmentPreviewStale")}
          </p>
        )}

        {preview && !previewIsStale && (
          <div className="space-y-2">
            <p className="text-xs text-c-gray-300">
              {preview.total == null
                ? t("campaign.segmentPreviewUnknownTotal")
                : t("campaign.segmentPreviewTotal", { total: preview.total })}
            </p>
            {preview.rows.length === 0 ? (
              <p className="text-c-gray-400 text-xs">
                {t("campaign.segmentPreviewEmpty")}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">ID</TableHead>
                    <TableHead className="text-xs">
                      {t("campaign.segmentPreviewName")}
                    </TableHead>
                    <TableHead className="text-xs">
                      {t("campaign.segmentPreviewPhone")}
                    </TableHead>
                    <TableHead className="text-xs text-right">
                      {t("campaign.segmentPreviewBalance")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow key={row.customerId}>
                      <TableCell className="text-xs py-1">
                        {row.customerId}
                      </TableCell>
                      <TableCell className="text-xs py-1">
                        {[row.firstName, row.lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </TableCell>
                      <TableCell className="text-xs py-1">
                        {row.phone || "—"}
                      </TableCell>
                      <TableCell className="text-xs py-1 text-right">
                        {row.minutesBalance}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** First line of the query, which is what identifies it in a one-line list. */
function describeDef(d) {
  const first =
    String(d.query || "")
      .trim()
      .split("\n")[0] || "—";
  return first.length > 80 ? `${first.slice(0, 79)}…` : first;
}

/** Campaigns that select this segment, so deleting it is not a silent change. */
function usedByText(usedBy, t) {
  const list = usedBy || [];
  if (list.length === 0) return t("campaign.segmentUnused");
  return t("campaign.segmentUsedBy", {
    campaigns: list
      .map((c) => (c.name ? `${c.name} (#${c.id})` : `#${c.id}`))
      .join(", "),
  });
}

/**
 * Whether a preview still describes what is in the editor.
 *
 * The server answers with the statement it actually ran — trimmed, without the
 * trailing semicolon most editors add on paste — so both sides are normalised
 * the same way before being compared.
 */
function sameQuery(a, b) {
  const normalise = (q) =>
    String(q || "")
      .trim()
      .replace(/;$/, "")
      .trim();
  return normalise(a) === normalise(b);
}

function describe(err, fallback) {
  if (typeof err === "string" && err.trim()) return err;
  if (err?.message) return err.message;
  return fallback;
}

/**
 * The backend's human-readable reason from an `apiFetch` failure.
 *
 * The API helpers `throw await resp.text()`, so the useful part lives in the
 * JSON body's `detail` — shown as-is it was `{"detail":"…"}` in a toast.
 */
function parseApiError(err) {
  const raw = typeof err === "string" ? err.trim() : "";
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === "string") return parsed.detail;
  } catch {
    // Not JSON — fall through to the raw text.
  }
  return raw;
}

/** Copy the segment query currently in the textarea. */
async function copyQuery(query, t) {
  if (!query.trim()) return;
  try {
    await navigator.clipboard.writeText(query);
    toast.success(t("campaign.segmentSqlCopied"));
  } catch {
    toast.error(t("campaign.sqlCopyFailed"));
  }
}
